import React from 'react';
import styled from '@emotion/styled';
import { CircleSeriesNode, CircleTopicFormat } from '@modernhealth/circles-graphql';
import {
  CircleCard,
  CircleCardLink,
  SeriesAccessType,
  SessionTime,
} from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { BodyTextSmall, BodyTextXSmall } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { Link } from 'react-router-dom';

import { useTranslation } from 'src/shared/hooks/formatMessage';

import { formatSeriesAccessType } from '../utils/formatSeriesAccessType';

export interface CircleSessionStylizedCardProps {
  session: {
    startTime?: string | null;
    facilitators: Array<{ photo?: string | null } | null>;
    topic: {
      name: string;
      slug?: string | null;
      format: CircleTopicFormat;
      series?: Partial<CircleSeriesNode>;
      duration?: number;
    };
  };
}

const SubHeader = styled.span({
  display: 'flex',
  alignItems: 'center',
});

const CardLink: React.FC<{ slug: string; name: string; size?: number }> = ({
  slug,
  name,
  size,
}) => (
  <Link
    css={{
      fontSize: size ?? 'inherit',
      fontWeight: size ? 500 : 400,
      color: size ? colors.gray65 : colors.gray85,
      '&:hover': {
        color: colors.gray85,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
      },
    }}
    to={`/circle/${slug}`}
    aria-describedby={`topic-id-${slug}`}
  >
    {name}
  </Link>
);

export const CircleStylizedSessionCard: React.FC<CircleSessionStylizedCardProps> = ({
  session,
  children,
}) => {
  const photos = filterNullish(session.facilitators.map(facilitator => facilitator?.photo) ?? []);
  const seriesName = session.topic.series?.name;
  const isMemberOnly =
    session.topic.series?.accessType === SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY;
  const { $t } = useTranslation();
  const eyebrow = (
    <SubHeader>
      {isMemberOnly && (
        <BodyTextXSmall
          as="span"
          fontColor="violet55"
          css={{ fontWeight: 500, marginRight: 4, gap: 4 }}
        >
          {$t(formatSeriesAccessType(SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY))}•
        </BodyTextXSmall>
      )}

      {seriesName && (
        <CardLink name={seriesName.toUpperCase()} slug={session.topic.slug!} size={12} />
      )}
    </SubHeader>
  );

  return (
    <CircleCard
      format={session.topic.format}
      photos={photos}
      eyebrow={eyebrow}
      header={<CardLink name={session.topic.name} slug={session.topic.slug!} />}
      body={
        <BodyTextSmall as="span">
          <SessionTime time={session.startTime!} />
        </BodyTextSmall>
      }
    >
      <CircleCardLink component={<span aria-hidden css={{ marginTop: 8 }} />}>
        {children}
      </CircleCardLink>
    </CircleCard>
  );
};
